import { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { segment } from '@actions/analytics';
import EducatorDetailedCard from '@comp/EducatorDetailedCard/EducatorDetailedCard';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Link from '@base/Link/Link';
import Grow from '@base/Grow/Grow';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import Button from '@base/Button/Button';
import { useRouter } from 'next/router';
import { SYLLABUS_SEE_ALL_CLICKED } from '@constants/segment';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import Typography from 'aquilla/core/atoms/Typography';

// PlusHome, FreeCourse, SpecialClasses

const StyH4 = styled(H4)`
  text-transform: capitalize;
  color: var(--color-text-primary);
`;
const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
`;
const LabelDiv = styled.div`
  grid-column: span 18;
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_7B};
  align-items: center;
`;

const Wrapper = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(18, 32px);
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  grid-auto-rows: max-content;
  ${mediaQuery.uptoMobile} {
    width: calc(100% + 16px);
    padding: 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_9B} 0;
    display: grid;
    grid-template-columns: repeat(21, 32px);
    grid-column-gap: 16px;
    grid-row-gap: 0;
    overflow: scroll;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    & > a {
      grid-column: span 7;
      height: auto;
    }
    &.wrapper {
      grid-template-columns: repeat(10, 1fr);
      grid-column-gap: 0;
      grid-row-gap: 16px;
      & > a {
        grid-column: span 10;
        height: auto;
      }
    }
  }
`;

const SeeAll = styled(Button)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    min-height: 40px;
    height: 40px;
    width: 100%;
    background: var(--color-i-white);
    border: 1px solid var(--color-divider);
    border-radius: 8px;
    color: var(--color-i-green);
    font-size: 14px;
    margin: 16px 0 56px;
  }
`;

const InfoSectionsDiv = styled.div``;

const Description = styled(Typography)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
`;

const EducatorFeedBlock = ({
  educatorFeedBlock,
  viewAllLink,
  viewAllHref,
  dataAnalytics,
  lastPrimarySource,
  useLeaderboardMetaInfo
}) => {
  const { isMobile } = useWindowSize();
  const { route } = useRouter();
  const { label, description } = educatorFeedBlock;
  const { selectedGoal: goal } = useSelectedGoal();
  const viewRef = useRef();
  const wrapperClass = route === '/goal/[goalSlug]/[goalUID]' ? 'wrapper' : '';
  // const count = educatorFeedBlock?.extraBlockinfo?.count;
  // const feedBlockType = educatorFeedBlock.type;
  // const seeAllLink =
  //   count > 3 ? `/goal/${goalSlug}/${goalUID}/${feedBlockType}` : null;
  // const href = '/goal/[goalSlug]/[goalUID]/[type]';
  const educators = educatorFeedBlock.users
    .slice(0, 4)
    .map((educatordata, _index) => {
      if (isMobile && _index > 2 && viewAllLink) {
        return null;
      }
      const name = educatordata.firstName + educatordata?.lastName;
      return (
        <EducatorDetailedCard
          label={label}
          educator={educatordata}
          lastPrimarySource={lastPrimarySource}
          key={name}
          index={_index}
          gridColumn="span 9"
          dataAnalytics={dataAnalytics}
          className={
            educatorFeedBlock.users?.length - 1 === _index ? 'last' : ''
          }
          linkStyle={
            _index > 1 &&
            `margin-bottom: ${Spacings.SPACING_8B};@media only screen and (max-width:47.95em){margin-bottom:0;}`
          }
          useLeaderboardMetaInfo={useLeaderboardMetaInfo}
          showBadge
        />
      );
    });

  const seeAllAnalytics = () => {
    segment.track(SYLLABUS_SEE_ALL_CLICKED, {
      expansion_type: educatorFeedBlock.label,
      goal_name: goal?.name || 'NA',
      goal_uid: goal?.uid || 'NA',
      subscription_duration: 'NA',
      subscription_end_date: 'NA',
      last_primary_source: lastPrimarySource,
      last_primary_source_section: educatorFeedBlock.label,
      current_screen: 'See All - Educators'
    });
  };
  const onClickSeeAll = () => {
    seeAllAnalytics();
    pushRoute(viewAllHref, viewAllLink);
  };
  return (
    <>
      <LabelDiv ref={viewRef}>
        <InfoSectionsDiv>
          <StyH4 as="h3">{label}</StyH4>
          {description && <Description variant="p2">{description}</Description>}
        </InfoSectionsDiv>
        <Grow />
        <div>
          {viewAllLink && !isMobile && (
            <Link href={viewAllHref} as={viewAllLink} onClick={seeAllAnalytics}>
              <BoldP1 color="var(--color-i-green)">SEE ALL</BoldP1>
            </Link>
          )}
        </div>
      </LabelDiv>
      <Wrapper className={wrapperClass}>{educators}</Wrapper>
      {viewAllLink && isMobile ? (
        <SeeAll label="See All" onClick={onClickSeeAll} />
      ) : null}
    </>
  );
};

export default EducatorFeedBlock;

EducatorFeedBlock.propTypes = {
  educatorFeedBlock: PropTypes.objectOf(PropTypes.any).isRequired,
  viewAllLink: PropTypes.string.isRequired,
  viewAllHref: PropTypes.string.isRequired,
  dataAnalytics: PropTypes.string.isRequired,
  lastPrimarySource: PropTypes.string,
  useLeaderboardMetaInfo: PropTypes.bool
};

EducatorFeedBlock.defaultProps = {
  lastPrimarySource: '',
  useLeaderboardMetaInfo: false
};
